"use client";

import { useState } from "react";
import { APIProvider, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface NeighborhoodScores {
  walkabilityScore: number | null;
  transitScore: number | null;
  bikeScore: number | null;
  safetyScore: number | null;
  overallScore: number | null;
  description: string;
  dataSource: 'places_nearby_search' | 'walk_score' | 'fallback';
}

interface MapData {
  center: { lat: number; lng: number };
  formattedAddress: string;
  nearby: Array<{
    name: string;
    category: string;
    location: { lat: number; lng: number };
  }>;
  walkScore: number | null; // Kept for backward compatibility
  neighborhoodScores?: NeighborhoodScores; // New comprehensive scores
}

interface NeighborhoodMapClientProps {
  mapData: MapData;
  address: string;
}

const PropertyPin = () => (
  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  </div>
);

const AmenityPin = ({ category }: { category: string }) => {
  const getColor = () => {
    switch (category) {
      case 'park': return 'bg-green-500';
      case 'transit': return 'bg-purple-500';
      case 'school': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className={`w-4 h-4 ${getColor()} rounded-full animate-ping-slow`} />
  );
};

export default function NeighborhoodMapClient({ mapData, address }: NeighborhoodMapClientProps) {
  const [mapType, setMapType] = useState<'roadmap' | 'satellite' | 'hybrid'>('roadmap');
  const [selectedAmenity, setSelectedAmenity] = useState<any>(null);

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <div className="h-[400px] bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Google Maps API key not configured</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="h-[400px] bg-gray-100 overflow-hidden">
        <APIProvider 
          apiKey={apiKey}
          libraries={["places", "geometry"]}
        >
          <Map
            mapId={process.env.NEXT_PUBLIC_GOOGLE_MAP_ID}
            defaultCenter={mapData.center}
            defaultZoom={15}
            gestureHandling="cooperative"
            colorScheme="FOLLOW_SYSTEM"
            mapTypeId={mapType}
            disableDefaultUI={false}
            zoomControl={true}
            mapTypeControl={false}
            streetViewControl={true}
            fullscreenControl={true}
            className="w-full h-full"
          >
            {/* Property marker */}
            <AdvancedMarker 
              position={mapData.center}
              title={mapData.formattedAddress}
            >
              <PropertyPin />
            </AdvancedMarker>
            
            {/* Nearby amenities */}
            {mapData.nearby.map((place, index) => (
              <AdvancedMarker
                key={index}
                position={place.location}
                title={place.name}
                onClick={() => setSelectedAmenity(place)}
              >
                <AmenityPin category={place.category} />
              </AdvancedMarker>
            ))}
            
            {/* Info window for selected amenity */}
            {selectedAmenity && (
              <InfoWindow
                position={selectedAmenity.location}
                onCloseClick={() => setSelectedAmenity(null)}
              >
                <div className="p-2">
                  <h4 className="font-medium">{selectedAmenity.name}</h4>
                  <p className="text-sm text-gray-600 capitalize">{selectedAmenity.category}</p>
                </div>
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </div>
      
      {/* Map Controls */}
      <div className="absolute top-4 right-4 bg-white rounded-lg shadow-md overflow-hidden">
        <Tabs value={mapType} onValueChange={(value) => setMapType(value as any)}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="roadmap" className="text-xs py-1 px-2">
              Standard
            </TabsTrigger>
            <TabsTrigger value="satellite" className="text-xs py-1 px-2">
              Satellite
            </TabsTrigger>
            <TabsTrigger value="hybrid" className="text-xs py-1 px-2">
              Hybrid
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      

      
      {/* Map Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <svg className="h-3.5 w-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs text-gray-600">Map data ©2025 Google</span>
        </div>
        <Button 
          variant="link" 
          size="sm" 
          className="text-blue-600 p-0 h-auto"
          onClick={() => {
            const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
            window.open(url, '_blank');
          }}
        >
          View in Google Maps
        </Button>
      </div>
      
      {/* Neighborhood Information Section */}
      <div className="p-6 bg-white border-t border-gray-100">
        {/* Map Legend Section */}
        <div className="mb-6 bg-gray-50 rounded-xl p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">Map Legend</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-600 rounded-full flex-shrink-0" />
              <span className="text-sm text-gray-700">Current Property</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0" />
              <span className="text-sm text-gray-700">Parks</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full flex-shrink-0" />
              <span className="text-sm text-gray-700">Public Transit</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full flex-shrink-0" />
              <span className="text-sm text-gray-700">Schools</span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nearby Amenities */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-semibold mb-4">Nearby Amenities</h3>
            {mapData.nearby && mapData.nearby.length > 0 ? (
              <ul className="space-y-2">
                {mapData.nearby.map((place, index) => {
                  const getIcon = (category: string) => {
                    switch (category) {
                      case 'park': return '🌳';
                      case 'transit_station': return '🚇';
                      case 'school': return '🏫';
                      case 'hospital': return '🏥';
                      case 'shopping_mall': return '🛍️';
                      default: return '📍';
                    }
                  };
                  
                  const formatCategory = (category: string) => {
                    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  };
                  
                  return (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-lg">{getIcon(place.category)}</span>
                      <span className="flex-1">{place.name}</span>
                      <span className="text-sm text-gray-500">({formatCategory(place.category)})</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-gray-500 text-center py-4">
                <svg className="h-12 w-12 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p>No nearby amenities data available</p>
                <p className="text-sm">Check Google Maps for local businesses</p>
              </div>
            )}
          </div>
          
          {/* Neighborhood Scores */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-semibold mb-4">Neighborhood Scores</h3>
            <div className="space-y-4">
              {/* Walk Score */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Walk Score:</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        (mapData.neighborhoodScores?.walkabilityScore || mapData.walkScore)
                          ? (mapData.neighborhoodScores?.walkabilityScore || mapData.walkScore)! >= 70 ? 'bg-green-500' 
                            : (mapData.neighborhoodScores?.walkabilityScore || mapData.walkScore)! >= 50 ? 'bg-yellow-500' 
                            : 'bg-red-500'
                          : 'bg-gray-400'
                      }`}
                      style={{ width: `${mapData.neighborhoodScores?.walkabilityScore || mapData.walkScore || 0}%` }}
                    ></div>
                  </div>
                  <span className="font-semibold text-sm w-12">
                    {mapData.neighborhoodScores?.walkabilityScore || mapData.walkScore 
                      ? `${mapData.neighborhoodScores?.walkabilityScore || mapData.walkScore}/100` 
                      : '--/100'}
                  </span>
                </div>
              </div>
              
              {/* Transit Score - Now with real data */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Transit Score:</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        mapData.neighborhoodScores?.transitScore 
                          ? mapData.neighborhoodScores.transitScore >= 70 ? 'bg-green-500' 
                            : mapData.neighborhoodScores.transitScore >= 50 ? 'bg-yellow-500' 
                            : 'bg-red-500'
                          : 'bg-gray-400'
                      }`}
                      style={{ width: `${mapData.neighborhoodScores?.transitScore || 0}%` }}
                    ></div>
                  </div>
                  <span className={`font-semibold text-sm w-12 ${mapData.neighborhoodScores?.transitScore ? '' : 'text-gray-400'}`}>
                    {mapData.neighborhoodScores?.transitScore ? `${mapData.neighborhoodScores.transitScore}/100` : '--/100'}
                  </span>
                </div>
              </div>
              
              {/* Bike Score - Now with real data */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Bike Score:</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        mapData.neighborhoodScores?.bikeScore 
                          ? mapData.neighborhoodScores.bikeScore >= 70 ? 'bg-green-500' 
                            : mapData.neighborhoodScores.bikeScore >= 50 ? 'bg-yellow-500' 
                            : 'bg-red-500'
                          : 'bg-gray-400'
                      }`}
                      style={{ width: `${mapData.neighborhoodScores?.bikeScore || 0}%` }}
                    ></div>
                  </div>
                  <span className={`font-semibold text-sm w-12 ${mapData.neighborhoodScores?.bikeScore ? '' : 'text-gray-400'}`}>
                    {mapData.neighborhoodScores?.bikeScore ? `${mapData.neighborhoodScores.bikeScore}/100` : '--/100'}
                  </span>
                </div>
              </div>
              
              {/* Safety Rating - Now with real data */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Safety Rating:</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        mapData.neighborhoodScores?.safetyScore 
                          ? mapData.neighborhoodScores.safetyScore >= 70 ? 'bg-green-500' 
                            : mapData.neighborhoodScores.safetyScore >= 50 ? 'bg-yellow-500' 
                            : 'bg-red-500'
                          : 'bg-gray-400'
                      }`}
                      style={{ width: `${mapData.neighborhoodScores?.safetyScore || 0}%` }}
                    ></div>
                  </div>
                  <span className={`font-semibold text-sm w-12 ${mapData.neighborhoodScores?.safetyScore ? '' : 'text-gray-400'}`}>
                    {mapData.neighborhoodScores?.safetyScore ? `${mapData.neighborhoodScores.safetyScore}/100` : '--'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Dynamic Description */}
            {(mapData.neighborhoodScores?.description || mapData.walkScore) && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  {mapData.neighborhoodScores?.description || 
                   (mapData.walkScore && mapData.walkScore >= 90 ? 'Walker\'s Paradise - daily errands do not require a car' :
                    mapData.walkScore && mapData.walkScore >= 70 ? 'Very Walkable - most errands can be accomplished on foot' :
                    mapData.walkScore && mapData.walkScore >= 50 ? 'Somewhat Walkable - some errands can be accomplished on foot' :
                    mapData.walkScore && mapData.walkScore >= 25 ? 'Car-Dependent - most errands require a car' :
                    'Car-Dependent - all errands require a car')}
                </p>
                {mapData.neighborhoodScores?.dataSource && (
                  <p className="text-xs text-blue-600 mt-1">
                    Data source: {mapData.neighborhoodScores.dataSource === 'places_nearby_search' ? 'Google Places API' : 
                                  mapData.neighborhoodScores.dataSource === 'walk_score' ? 'Walk Score API' : 'Fallback'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Additional Neighborhood Insights */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-blue-800">Commute Info</span>
            </div>
            <p className="text-sm text-blue-700">
              Check Google Maps for real-time transit options and commute times to your workplace.
            </p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16l3-3m-3 3l-3-3" />
              </svg>
              <span className="font-medium text-green-800">Local Amenities</span>
            </div>
            <p className="text-sm text-green-700">
              {mapData.nearby && mapData.nearby.length > 0 
                ? `${mapData.nearby.length} nearby amenities found including ${mapData.nearby.slice(0, 2).map(p => p.category).join(', ')}.`
                : 'Explore the area using the map above for local businesses and services.'
              }
            </p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="font-medium text-purple-800">Area Insights</span>
            </div>
            <p className="text-sm text-purple-700">
              Walkability score helps determine how easy it is to accomplish daily tasks on foot in this neighborhood.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 